/**
 * Bitacora.tsx — quién hizo qué en los libros de esta empresa.
 *
 * ── ▶ ES LA DEUDA DEL PISO, Y POR ESO NO SE ESCONDE CUANDO NO SE PUEDE ────
 *
 * El piso de suspensión promete «leer y exportar sus periodos cerrados, sus
 * estados y **su bitácora**». Esta pantalla **no cuelga de `puedeRegistrar`**:
 * una empresa suspendida la ve entera, y es justo cuando más falta le hace —
 * ahí está la orden que la suspendió.
 *
 * La función existía desde E1, concedida y **sin que nada la llamara**: la
 * promesa vivía en el documento, en la migración y en ninguna otra parte. La
 * encontró el guardián 8.
 *
 * ── El texto lo pone el diccionario, no la base ───────────────────────────
 *
 * `accion` llega como clave —`empresa.creada`, `periodo.cerrado`— y la frase
 * sale de `bit.<accion>`. Una migración que devolviera «Se cerró el periodo»
 * llegaría en español a una pantalla en inglés. `pruebas/idioma.test.ts` saca
 * la lista de acciones de las migraciones y exige que cada una tenga rótulo.
 *
 * ── Y el detalle se muestra crudo, a propósito ────────────────────────────
 *
 * Cada acción anota lo suyo y su forma cambia con ella: `membresia.quitada`
 * guarda si había algo que quitar, `empresa.creada` guarda si es demo. Darle
 * una plantilla a cada una sería una segunda lista que se queda vieja el día
 * que alguien agregue un campo — y lo que se pierde entonces no es formato: es
 * el dato que alguien fue a buscar.
 */
import { useCallback, useEffect, useState } from 'react';
import { useIdioma, Texto } from '../nucleo/idioma.tsx';
import { useSesion } from '../nucleo/sesion.tsx';
import {
  traerBitacora, FalloDeApi, type EntradaDeBitacora,
} from '../datos/libros.ts';

/** Los pares del detalle, en orden estable. `null` y `false` también se muestran. */
function pares(detalle: Readonly<Record<string, unknown>>): Array<[string, string]> {
  return Object.entries(detalle)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => [k, v === null ? '—' : String(v)]);
}

export function Bitacora() {
  const { t, fecha: comoFecha } = useIdioma();
  const { pedir, sesion } = useSesion();

  const [entradas, setEntradas] = useState<readonly EntradaDeBitacora[] | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  // ▶ Aparte de las filas, porque es un hecho aparte: cuántas se pidieron y si
  // la base tenía más. `null` es «todavía no se sabe», que no es «no hay más».
  const [recorte, setRecorte] = useState<{ hayMas: boolean; limite: number } | null>(null);

  const empresaId = sesion?.empresa.id ?? null;

  const cargar = useCallback(async () => {
    setFallo(null);
    try {
      const r = await traerBitacora(pedir, {
        desde: desde || undefined,
        hasta: hasta || undefined,
      });
      setEntradas(r.filas);
      setRecorte({ hayMas: r.hayMas, limite: r.limite });
    } catch (e) {
      setEntradas(null);
      setRecorte(null);
      const f = e instanceof FalloDeApi ? e : null;
      setFallo(f?.clase === 'conexion' ? 'sb.conexion'
        : f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
    }
  }, [pedir, desde, hasta]);

  useEffect(() => {
    if (!empresaId) return;
    void cargar();
  }, [cargar, empresaId]);

  return (
    <div>
      <header className="page-head">
        <div>
          <h1>{t('bit.title')}</h1>
          <p className="small muted">{t('bit.sub')}</p>
        </div>
        <span className="spacer" />
        <label className="field field--inline">
          <span className="field__label">{t('bit.from')}</span>
          <input
            className="input input--mono" value={desde} placeholder="YYYY-MM-DD"
            onChange={(e) => setDesde(e.target.value.trim())}
          />
        </label>
        <label className="field field--inline">
          <span className="field__label">{t('bit.to')}</span>
          <input
            className="input input--mono" value={hasta} placeholder="YYYY-MM-DD"
            onChange={(e) => setHasta(e.target.value.trim())}
          />
        </label>
      </header>

      {/* ▶ El piso se DICE acá también. Quien está suspendido y entra a esta
          pantalla tiene que ver que sigue siendo suya, no preguntarse si lo
          que ve está recortado. */}
      {sesion?.capacidad === 'piso' && (
        <div className="alert" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t('bit.floor')}</span>
        </div>
      )}

      {fallo && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t(fallo as never)}</span>
        </div>
      )}

      {/* ▶ EL CORTE SE DICE, Y ACÁ MÁS QUE EN NINGUNA OTRA LISTA.
          A las otras dos se entra a mirar; a la bitácora se entra a BUSCAR un
          hecho concreto, casi siempre porque algo salió mal. Quien no lo
          encuentra en una lista que se presenta completa no concluye que está
          cortada: concluye que no ocurrió. Y como el orden es del más reciente
          al más viejo, lo que se pierde es justo lo viejo — que es lo que se
          suele venir a buscar. */}
      {recorte?.hayMas === true && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          {/* `Texto` y no `t()`: la frase lleva énfasis y `t()` pintaría los
              asteriscos tal cual. */}
          <span><Texto k="bit.trimmed" con={{ limite: String(recorte.limite) }} /></span>
        </div>
      )}

      <section className="card card--pad0">
        <div className="table-wrap table-wrap--alto">
          <table className="tbl">
            <thead>
              <tr>
                <th>{t('common.date')}</th>
                <th>{t('bit.action')}</th>
                <th>{t('bit.who')}</th>
                <th>{t('bit.detail')}</th>
              </tr>
            </thead>
            <tbody>
              {(entradas ?? []).map((e) => (
                <tr key={`${e.ocurrio_en}-${e.accion}`}>
                  <td className="mono small">
                    {comoFecha(e.ocurrio_en.slice(0, 10))}
                    <span className="muted"> {e.ocurrio_en.slice(11, 16)}</span>
                  </td>
                  <td>{t(`bit.${e.accion}` as never)}</td>
                  <td className="small">
                    {/* Sin nombre no se inventa uno: el hecho vale igual. */}
                    {/* ▶ «Nadie quedó anotado» y «la persona ya no está» son
                        DISTINTOS, y decían lo mismo.
                        Sin `usuario_id` no hubo persona: lo hizo un proceso
                        —una herramienta, una migración—. Con `usuario_id` y
                        sin nombre, la persona existió y su fila se borró. La
                        bitácora de la demo decía «usuario eliminado» de un
                        usuario que nunca existió. */}
                    {e.quien ?? (
                      <span className="muted">
                        {t(e.usuario_id === null ? 'common.system' : 'common.removedUser')}
                      </span>
                    )}
                    {e.correo && <span className="muted mono small"> {e.correo}</span>}
                  </td>
                  <td className="small mono">
                    {pares(e.detalle).map(([k, v]) => (
                      <span key={k} style={{ marginRight: 'var(--s4)' }}>
                        <span className="muted">{k}:</span> {v}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
              {entradas !== null && entradas.length === 0 && (
                <tr><td colSpan={4} className="muted">{t('bit.empty')}</td></tr>
              )}
              {entradas === null && !fallo && (
                <tr><td colSpan={4} className="muted">{t('common.loading')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <p className="field__hint" style={{ marginTop: 'var(--s6)' }}>{t('bit.why')}</p>
    </div>
  );
}
